import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { VISIT_LIFECYCLE_STATES } from '@/clinical-study/vocabulary';
import { FormFeedback } from '@/components/ui/form-feedback';
import { Skeleton, SkeletonRegion, TableSkeleton } from '@/components/ui/skeleton';
import { apiClient } from '@/lib/api-client';
import type { paths } from '@/lib/api-types-clinical-study';
import { todayLocalDate } from '@/lib/intl';
import { AtRiskVisitsTable, type AtRiskVisitEntry } from '../subjects/AtRiskVisitsTable';

type Overview =
  paths['/v1/clinical-study/studies/{studyId}/monitor/overview']['get']['responses'][200]['content']['application/json'];

type NameState =
  { status: 'loading' } | { status: 'unavailable' } | { status: 'ready'; name: string };
type OverviewState =
  | { status: 'loading' }
  | { status: 'forbidden' }
  | { status: 'error' }
  | { status: 'ready'; overview: Overview };
type AtRiskState =
  | { status: 'loading' }
  | { status: 'forbidden' }
  | { status: 'error' }
  | { status: 'ready'; entries: AtRiskVisitEntry[] };

// Resolution R11-style literal map, reusing the SAME `schedule.state*` i18n keys `ScheduleSection`
// already defines — no new keys for this column.
const FOLLOW_UP_STATE_LABEL_KEY = {
  PLANNED: 'schedule.statePLANNED',
  SCHEDULED: 'schedule.stateSCHEDULED',
  COMPLETED: 'schedule.stateCOMPLETED',
  MISSED: 'schedule.stateMISSED',
  CANCELLED: 'schedule.stateCANCELLED',
} as const;

/**
 * `/clinical-study/oversight/:studyId` (Resolution R2/R3): the CRO monitor's own per-study
 * oversight page, driven entirely by `clinical-study:study:monitor`-gated reads. The study-name
 * fetch (`GET /v1/clinical-study/studies/{studyId}`, `clinical-study:study:manage`-gated) is
 * NEVER a precondition for the overview/at-risk data below it — a `NameState` failure degrades
 * to a plain heading, never blocking the rest of the page. No `onDrillDown` is passed to
 * `AtRiskVisitsTable` — the CRO monitor has no subject workspace to open (Resolution R1).
 */
export function StudyOversightPage() {
  const { t } = useTranslation('clinical-study');
  const { studyId } = useParams<{ studyId: string }>();
  const [nameState, setNameState] = useState<NameState>({ status: 'loading' });
  const [overviewState, setOverviewState] = useState<OverviewState>({ status: 'loading' });
  const [atRiskState, setAtRiskState] = useState<AtRiskState>({ status: 'loading' });

  useEffect(() => {
    if (studyId === undefined) {
      return;
    }
    setNameState({ status: 'loading' });
    apiClient
      .GET('/v1/clinical-study/studies/{studyId}', { params: { path: { studyId } } })
      .then(({ data, error, response }) => {
        if (error !== undefined || !response.ok || data === undefined) {
          setNameState({ status: 'unavailable' });
          return;
        }
        setNameState({ status: 'ready', name: data.name });
      })
      .catch(() => setNameState({ status: 'unavailable' }));
  }, [studyId]);

  useEffect(() => {
    if (studyId === undefined) {
      return;
    }
    setOverviewState({ status: 'loading' });
    apiClient
      .GET('/v1/clinical-study/studies/{studyId}/monitor/overview', {
        params: { path: { studyId }, query: { asOfDate: todayLocalDate() } },
      })
      .then(({ data, error, response }) => {
        if (response.status === 403) {
          setOverviewState({ status: 'forbidden' });
          return;
        }
        if (error !== undefined || !response.ok || data === undefined) {
          setOverviewState({ status: 'error' });
          return;
        }
        setOverviewState({ status: 'ready', overview: data });
      })
      .catch(() => setOverviewState({ status: 'error' }));
  }, [studyId]);

  useEffect(() => {
    if (studyId === undefined) {
      return;
    }
    setAtRiskState({ status: 'loading' });
    apiClient
      .GET('/v1/clinical-study/studies/{studyId}/monitor/at-risk-visits', {
        params: { path: { studyId }, query: { asOfDate: todayLocalDate() } },
      })
      .then(({ data, error, response }) => {
        if (response.status === 403) {
          setAtRiskState({ status: 'forbidden' });
          return;
        }
        if (error !== undefined || !response.ok || data === undefined) {
          setAtRiskState({ status: 'error' });
          return;
        }
        setAtRiskState({ status: 'ready', entries: data });
      })
      .catch(() => setAtRiskState({ status: 'error' }));
  }, [studyId]);

  if (studyId === undefined) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <Link to="/clinical-study/oversight" className="text-sm underline">
          {t('oversight.backToEntry')}
        </Link>
        {nameState.status === 'loading' && (
          <SkeletonRegion>
            {/* Sized to a study name so the heading does not resize under the reader's eye when the
                real one lands. */}
            <Skeleton className="my-[3px] h-5 w-64" />
          </SkeletonRegion>
        )}
        {nameState.status === 'unavailable' && (
          <h2 className="screen-title">{t('oversight.nameUnavailable')}</h2>
        )}
        {nameState.status === 'ready' && <h2 className="screen-title">{nameState.name}</h2>}
        <p className="text-sm">
          {t('oversight.idLabel')}: <code>{studyId}</code>
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('oversight.overviewTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {overviewState.status === 'loading' && (
            <SkeletonRegion className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              {Array.from({ length: 6 }, (_unused, figure) => (
                <div key={figure} className="space-y-2">
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-5 w-12" />
                </div>
              ))}
            </SkeletonRegion>
          )}
          {overviewState.status === 'forbidden' && (
            <FormFeedback tone="error">{t('oversight.overviewForbiddenMessage')}</FormFeedback>
          )}
          {overviewState.status === 'error' && (
            <FormFeedback tone="error">{t('oversight.overviewLoadError')}</FormFeedback>
          )}
          {overviewState.status === 'ready' && (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <div>
                <p className="card-detail">{t('oversight.enrollmentProvisionalLabel')}</p>
                <p className="card-value">
                  {overviewState.overview.enrollmentProgress.provisional}
                </p>
              </div>
              <div>
                <p className="card-detail">{t('oversight.enrollmentEnrolledLabel')}</p>
                <p className="card-value">{overviewState.overview.enrollmentProgress.enrolled}</p>
              </div>
              <div>
                <p className="card-detail">{t('oversight.enrollmentCancelledLabel')}</p>
                <p className="card-value">{overviewState.overview.enrollmentProgress.cancelled}</p>
              </div>
              <div>
                <p className="card-detail">{t('oversight.treatmentsPerformedLabel')}</p>
                <p className="card-value">{overviewState.overview.treatmentsPerformed}</p>
              </div>
              <div className="col-span-2 sm:col-span-3">
                <p className="card-detail">{t('oversight.followUpStatusLabel')}</p>
                <ul className="flex flex-wrap gap-3 text-sm">
                  {VISIT_LIFECYCLE_STATES.map((visitState) => (
                    <li key={visitState}>
                      {t(FOLLOW_UP_STATE_LABEL_KEY[visitState])}:{' '}
                      {overviewState.overview.followUpVisitStatus[visitState] ?? 0}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="col-span-2 sm:col-span-3">
                <p className="card-detail">{t('oversight.atRiskCountLabel')}</p>
                <p className="card-value">{overviewState.overview.atRiskCount}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('atRisk.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {atRiskState.status === 'loading' && <TableSkeleton columns={6} />}
          {atRiskState.status === 'forbidden' && (
            <FormFeedback tone="error">{t('atRisk.forbiddenMessage')}</FormFeedback>
          )}
          {atRiskState.status === 'error' && (
            <FormFeedback tone="error">{t('atRisk.loadError')}</FormFeedback>
          )}
          {atRiskState.status === 'ready' && <AtRiskVisitsTable entries={atRiskState.entries} />}
        </CardContent>
      </Card>
    </div>
  );
}
